import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import { GenericExternalLink } from "@components/generic-external-link";
import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { SecondaryButton, TertiaryButton } from "@shared/buttons";
import { TextAreaInput } from "@shared/textarea-input";

interface Props {
  isOpen: boolean;
  defaultValue: string;
  onOpenChange(value: boolean): void;
  onApply(val: string): void;
  onCancel(): void;
}

export const SearchMethodRegexPopover = ({
  isOpen,
  defaultValue,
  onOpenChange,
  onApply,
  onCancel,
}: Props) => {
  const [value, setValue] = useState(defaultValue);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    transform: false,
    placement: "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
    }
  }, [isOpen, defaultValue]);

  return (
    <div className="w-full truncate">
      <button
        className="flex items-center font-normal h-[30px] w-full"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <i
          className={clsx(
            "fa-regular fa-sharp fa-function",
            "text-[0.75rem] text-gray-0 ml-0.5 mr-1.5",
          )}
        ></i>
        <div className="text-sm truncate">{defaultValue}</div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute left-0 z-10 mt-0.5 px-4 pb-4 pt-3 w-full",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            {...getFloatingProps()}
          >
            <h4 className="text-sm font-semibold text-interstellar+2 mb-2.5">
              regEx search
            </h4>
            <p className="text-xs font-light text-gray+1 mb-3">
              Search using regular expressions{" "}
              <GenericExternalLink href="https://regexr.com/">
                Learn more
              </GenericExternalLink>
            </p>
            <div>
              <TextAreaInput
                placeholder="Type here..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                resizable
              />
            </div>
            <div className="mt-2 flex justify-end">
              <TertiaryButton
                className="mr-2 font-semibold"
                onClick={() => onCancel()}
              >
                CANCEL
              </TertiaryButton>
              <SecondaryButton
                isDisabled={value === ""}
                className="font-semibold"
                onClick={() => {
                  onApply(value);
                }}
              >
                APPLY
              </SecondaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
